(function () {
  if (!window.customElements || window.customElements.get('free-gift-milestone')) {
    return;
  }

  class FreeGiftMilestone extends HTMLElement {
    constructor() {
      super();
      this._processing = false;
      this._pendingCart = null;
      this._isFetchingCart = false;
      this._fetchQueued = false;

      this._onCartChangedListener = this._onCartChanged.bind(this);
      this._onCartRefreshListener = this._onCartRefresh.bind(this);
      this._onVariantAddedListener = this._onVariantAdded.bind(this);
      this._onDrawerShownListener = this._onDrawerShown.bind(this);
      this._onPageShowListener = this._onPageShow.bind(this);

      this._currencyFormatter = this._createCurrencyFormatter();
      this._numberFormatter = this._createNumberFormatter();
    }

    connectedCallback() {
      this._messageElement = this.querySelector('[data-free-gift-message]');
      this._helperElement = this.querySelector('[data-free-gift-helper]');
      this._currentAmountElement = this.querySelector('[data-free-gift-current]');
      this._targetAmountElement = this.querySelector('[data-free-gift-target]');
      this._progressTrackElement = this.querySelector('[data-free-gift-progress]');
      this._progressFillElement = this.querySelector('[data-free-gift-progress-fill]');

      document.addEventListener('cart:change', this._onCartChangedListener);
      document.addEventListener('cart:refresh', this._onCartRefreshListener);
      document.addEventListener('variant:add', this._onVariantAddedListener);
      document.addEventListener('dialog:after-show', this._onDrawerShownListener);
      window.addEventListener('pageshow', this._onPageShowListener);

      this._updateFromServer();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:change', this._onCartChangedListener);
      document.removeEventListener('cart:refresh', this._onCartRefreshListener);
      document.removeEventListener('variant:add', this._onVariantAddedListener);
      document.removeEventListener('dialog:after-show', this._onDrawerShownListener);
      window.removeEventListener('pageshow', this._onPageShowListener);
    }

    get giftVariantId() {
      return parseInt(this.getAttribute('gift-variant-id'), 10) || 0;
    }

    get autoManageGift() {
      return (this.getAttribute('auto-manage') || 'true').toLowerCase() !== 'false';
    }

    get useShippingTeaser() {
      return (this.getAttribute('use-shipping-teaser') || 'true').toLowerCase() !== 'false';
    }

    get hideCurrencyPrefix() {
      return (this.getAttribute('hide-currency-prefix') || 'false').toLowerCase() === 'true';
    }

    _onCartChanged(event) {
      const cart = event && event.detail ? event.detail.cart : null;
      if (!cart) {
        return;
      }

      this._handleIncomingCart(cart);
    }

    _onCartRefresh() {
      this._updateFromServer();
    }

    _onVariantAdded(event) {
      if (event && event.detail && event.detail.cart) {
        this._handleIncomingCart(event.detail.cart);
      } else {
        this._updateFromServer();
      }
    }

    _onDrawerShown(event) {
      if (event && event.target && event.target.id === 'cart-drawer') {
        this._updateFromServer();
      }
    }

    _onPageShow(event) {
      if (event.persisted) {
        this._updateFromServer();
      }
    }

    async _updateFromServer() {
      if (this._isFetchingCart) {
        this._fetchQueued = true;
        return;
      }

      this._isFetchingCart = true;

      try {
        const cart = await this._fetchCart();
        this._handleIncomingCart(cart);
      } catch (error) {
        console.warn('[free-gift-milestone] Unable to refresh cart state.', error);
      } finally {
        this._isFetchingCart = false;

        if (this._fetchQueued) {
          this._fetchQueued = false;
          this._updateFromServer();
        }
      }
    }

    _handleIncomingCart(cart) {
      const snapshot = this._createSnapshot(cart);
      this._render(snapshot);

      if (!this.autoManageGift || this._processing || this.giftVariantId <= 0 || snapshot.unlockThreshold <= 0) {
        if (this._processing) {
          this._pendingCart = cart;
        }
        return;
      }

      this._syncGiftRules(cart);
    }

    async _syncGiftRules(cart) {
      this._processing = true;

      try {
        const syncedCart = await this._applyGiftRules(cart);

        if (syncedCart) {
          const sections = this._collectBundledSections();
          const cartWithSections = await this._ensureSections(syncedCart, sections);
          this._dispatchCartChange(cartWithSections);
        }
      } catch (error) {
        console.warn('[free-gift-milestone] Unable to synchronize gift lines.', error);
      } finally {
        this._processing = false;

        if (this._pendingCart) {
          const pending = this._pendingCart;
          this._pendingCart = null;
          this._handleIncomingCart(pending);
        }
      }
    }

    async _applyGiftRules(cart) {
      const snapshot = this._createSnapshot(cart);
      const giftLines = snapshot.giftLines;
      const giftQuantity = giftLines.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 0), 0);

      if (snapshot.eligibleSubtotal >= snapshot.unlockThreshold) {
        if (giftQuantity === 0) {
          return this._addGiftToCart();
        }

        if (giftQuantity > 1 || giftLines.length > 1) {
          return this._normalizeGiftLines(giftLines);
        }

        return null;
      }

      if (giftQuantity > 0) {
        return this._removeGiftLines(giftLines);
      }

      return null;
    }

    async _addGiftToCart() {
      const sections = this._collectBundledSections();
      const payload = new FormData();

      payload.set('id', String(this.giftVariantId));
      payload.set('quantity', '1');

      if (sections.length > 0) {
        payload.set('sections', sections.join(','));
        payload.set('sections_url', window.location.pathname);
      }

      const response = await fetch(`${this._cartRoot()}cart/add.js`, {
        method: 'POST',
        body: payload,
        headers: {
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to add gift item.');
      }

      const addResponse = await response.json();
      const cart = await this._fetchCart();

      if (addResponse && addResponse.sections) {
        cart.sections = addResponse.sections;
      }

      return cart;
    }

    async _normalizeGiftLines(giftLines) {
      const changes = [];

      giftLines.forEach((lineItem, index) => {
        const targetQuantity = index === 0 ? 1 : 0;
        const currentQuantity = parseInt(lineItem.quantity, 10) || 0;

        if (currentQuantity !== targetQuantity) {
          changes.push({
            key: lineItem.key,
            quantity: targetQuantity
          });
        }
      });

      if (changes.length === 0) {
        return null;
      }

      return this._runLineChanges(changes);
    }

    async _removeGiftLines(giftLines) {
      const changes = giftLines
        .filter((lineItem) => (parseInt(lineItem.quantity, 10) || 0) > 0)
        .map((lineItem) => ({
          key: lineItem.key,
          quantity: 0
        }));

      if (changes.length === 0) {
        return null;
      }

      return this._runLineChanges(changes);
    }

    async _runLineChanges(changes) {
      let cartResponse = null;

      for (let index = 0; index < changes.length; index += 1) {
        const lineChange = changes[index];
        const includeSections = index === changes.length - 1;
        cartResponse = await this._changeLineItem(lineChange.key, lineChange.quantity, includeSections);
      }

      return cartResponse;
    }

    async _changeLineItem(lineKey, quantity, includeSections) {
      const payload = {
        id: lineKey,
        quantity: quantity
      };

      if (includeSections) {
        const sections = this._collectBundledSections();

        if (sections.length > 0) {
          payload.sections = sections;
          payload.sections_url = window.location.pathname;
        }
      }

      const response = await fetch(`${this._cartRoot()}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        throw new Error('Failed to update gift line item.');
      }

      return response.json();
    }

    _createSnapshot(cart) {
      const unlockThreshold = this._resolveThreshold('gift-threshold');
      const teaserThresholdRaw = this._resolveThreshold('teaser-threshold');
      const teaserThreshold = this.useShippingTeaser ? Math.min(teaserThresholdRaw, unlockThreshold) : 0;
      const giftVariantId = this.giftVariantId;

      let eligibleSubtotal = 0;
      const giftLines = [];
      const items = cart && Array.isArray(cart.items) ? cart.items : [];

      items.forEach((item) => {
        if (giftVariantId > 0 && parseInt(item.variant_id, 10) === giftVariantId) {
          giftLines.push(item);
          return;
        }

        eligibleSubtotal += parseInt(item.final_line_price, 10) || 0;
      });

      const cartLevelDiscount = ((cart && cart.cart_level_discount_applications) || []).reduce((sum, discount) => {
        return sum + (parseInt(discount.total_allocated_amount, 10) || 0);
      }, 0);

      eligibleSubtotal = Math.max(0, eligibleSubtotal - cartLevelDiscount);

      return {
        eligibleSubtotal: eligibleSubtotal,
        teaserThreshold: teaserThreshold,
        unlockThreshold: unlockThreshold,
        giftLines: giftLines
      };
    }

    _render(snapshot) {
      const unlockThreshold = Math.max(snapshot.unlockThreshold, 1);
      const teaserThreshold = Math.max(0, Math.min(snapshot.teaserThreshold, unlockThreshold));
      const subtotal = snapshot.eligibleSubtotal;
      const giftLabel = this.getAttribute('gift-label') || 'Free gift';

      let template = this.getAttribute('message-unlocked') || '';
      let remainingAmount = 0;

      if (this.useShippingTeaser && subtotal < teaserThreshold) {
        remainingAmount = teaserThreshold - subtotal;
        template = this.getAttribute('message-before-teaser') || template;
      } else if (subtotal < unlockThreshold) {
        remainingAmount = unlockThreshold - subtotal;
        template = this.getAttribute('message-before-unlock') || template;
      }

      const tokens = {
        amount: this._formatMoney(Math.max(remainingAmount, 0)),
        gift: giftLabel,
        threshold: this._formatMoney(unlockThreshold),
        teaser_threshold: this._formatMoney(teaserThreshold)
      };

      if (this._messageElement) {
        this._messageElement.textContent = this._replaceTokens(template, tokens);
      }

      if (this._helperElement) {
        const helperTemplate = this.getAttribute('helper-template') || '';
        if (helperTemplate.trim() === '') {
          this._helperElement.hidden = true;
        } else {
          this._helperElement.hidden = false;
          this._helperElement.textContent = this._replaceTokens(helperTemplate, tokens);
        }
      }

      if (this._currentAmountElement) {
        this._currentAmountElement.textContent = this._formatMoney(subtotal);
      }

      if (this._targetAmountElement) {
        this._targetAmountElement.textContent = this._formatMoney(unlockThreshold);
      }

      if (this._progressTrackElement) {
        const clampedSubtotal = Math.min(subtotal, unlockThreshold);
        this._progressTrackElement.setAttribute('aria-valuemax', String(unlockThreshold));
        this._progressTrackElement.setAttribute('aria-valuenow', String(clampedSubtotal));
      }

      if (this._progressFillElement) {
        const progressPercent = Math.max(0, Math.min(100, (subtotal / unlockThreshold) * 100));
        this._progressFillElement.style.width = `${progressPercent}%`;
      }
    }

    _replaceTokens(template, tokenMap) {
      let output = template || '';

      Object.keys(tokenMap).forEach((token) => {
        const safeToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const replacement = tokenMap[token];

        output = output.replace(new RegExp(`\\{\\{\\s*${safeToken}\\s*\\}\\}`, 'gi'), replacement);
        output = output.replace(new RegExp(`\\[\\[\\s*${safeToken}\\s*\\]\\]`, 'gi'), replacement);
        output = output.replace(new RegExp(`\\[\\s*${safeToken}\\s*\\]`, 'gi'), replacement);
      });

      return output;
    }

    _resolveThreshold(attributeName) {
      const baseValue = parseInt(this.getAttribute(attributeName), 10) || 0;
      const currencyRate = Number(window.Shopify && Shopify.currency ? Shopify.currency.rate : 1) || 1;
      return Math.max(0, Math.round(baseValue * currencyRate));
    }

    _formatMoney(cents) {
      const normalizedValue = Math.max(0, cents || 0);
      const decimalValue = normalizedValue / 100;

      if (this.hideCurrencyPrefix) {
        if (this._numberFormatter) {
          return `$${this._numberFormatter.format(decimalValue)}`;
        }

        return `$${decimalValue.toFixed(2)}`;
      }

      if (this._currencyFormatter) {
        return this._currencyFormatter.format(decimalValue);
      }

      return `$${decimalValue.toFixed(2)}`;
    }

    _createCurrencyFormatter() {
      const locale = window.Shopify && Shopify.locale ? Shopify.locale : document.documentElement.lang || 'en';
      const currency = window.Shopify && Shopify.currency && Shopify.currency.active ? Shopify.currency.active : 'USD';

      try {
        return new Intl.NumberFormat(locale, { style: 'currency', currency: currency });
      } catch (_error) {
        return null;
      }
    }

    _createNumberFormatter() {
      const locale = window.Shopify && Shopify.locale ? Shopify.locale : document.documentElement.lang || 'en';

      try {
        return new Intl.NumberFormat(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      } catch (_error) {
        return null;
      }
    }

    _collectBundledSections() {
      const sections = [];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sections }
        })
      );

      return Array.from(new Set(sections.filter(Boolean)));
    }

    async _ensureSections(cart, requestedSections) {
      const sections = requestedSections || [];

      if (sections.length === 0) {
        return cart;
      }

      if (cart && cart.sections && Object.keys(cart.sections).length > 0) {
        return cart;
      }

      try {
        const url = `${this._cartRoot()}?sections=${encodeURIComponent(sections.join(','))}`;
        const response = await fetch(url);

        if (!response.ok) {
          return cart;
        }

        const sectionsPayload = await response.json();
        cart.sections = sectionsPayload;
      } catch (_error) {
      }

      return cart;
    }

    _dispatchCartChange(cart) {
      if (cart && cart.sections && Object.keys(cart.sections).length > 0) {
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:change', {
            bubbles: true,
            detail: {
              baseEvent: 'variant:add',
              cart: cart
            }
          })
        );
      } else {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    }

    async _fetchCart() {
      const response = await fetch(`${this._cartRoot()}cart.js`);

      if (!response.ok) {
        throw new Error('Failed to fetch cart');
      }

      return response.json();
    }

    _cartRoot() {
      return window.Shopify && Shopify.routes && Shopify.routes.root ? Shopify.routes.root : '/';
    }
  }

  window.customElements.define('free-gift-milestone', FreeGiftMilestone);
})();
